import React, { useState, useEffect } from "react";
import { api, currency, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { Users, Package, FileText, ShoppingBag, Star, LayoutDashboard, Shield, BookOpen } from "lucide-react";
import { AdminCMSPane } from "./AdminCMS";

const STATUS_ACTIONS = ["LIVE", "APPROVED", "CHANGES_REQUESTED", "REJECTED", "SUSPENDED"];

export default function AdminPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  useEffect(() => { if (user && ["ADMIN","SUPER_ADMIN"].includes(user.role)) api.get("/admin/stats").then(r => setStats(r.data)); }, [user, tab]);

  if (!user) return null;
  if (user === false || !["ADMIN","SUPER_ADMIN"].includes(user.role)) return <Navigate to="/" replace />;

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "experts", label: "Experts", icon: Users },
    { id: "partners", label: "Partners", icon: Users },
    { id: "products", label: "Products", icon: Package },
    { id: "users", label: "Users", icon: Shield },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "cms", label: "Content", icon: BookOpen },
  ];

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6"><div className="tl-label mb-1">Admin</div><h1 className="text-3xl font-display font-bold">Trinity Admin</h1></div>
      <div className="flex overflow-x-auto no-scrollbar gap-2 mb-6 border-b border-slate-200">
        {tabs.map(t => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`shrink-0 flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${tab === t.id ? "border-[#0891B2] text-slate-900" : "border-transparent text-[#475569]"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(stats).map(([k, v]) => (
            <div key={k} className="tl-card p-5">
              <div className="tl-label mb-2">{k.replace(/_/g, " ")}</div>
              <div className="font-display text-2xl font-bold">{typeof v === "number" && k === "revenue" ? currency(v) : String(v)}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "experts" && <ModerationList endpoint="experts" fields={["name", "category", "location", "status"]} />}
      {tab === "partners" && <ModerationList endpoint="partners" fields={["business_name", "location", "status"]} />}
      {tab === "products" && <ModerationList endpoint="products" fields={["name", "category", "price", "creator_name", "status"]} />}
      {tab === "users" && <UsersList />}
      {tab === "orders" && <OrdersList />}
      {tab === "reviews" && <ReviewsList />}
      {tab === "cms" && <AdminCMSPane />}
    </div>
  );
}

function ModerationList({ endpoint, fields }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const load = () => api.get(`/admin/${endpoint}${status !== "all" ? `?status=${status}` : ""}`).then(r => setItems(r.data));
  useEffect(load, [endpoint, status]);
  const update = async (id, newStatus) => {
    try { await api.put(`/admin/${endpoint}/${id}/status`, { status: newStatus }); toast.success(`Updated to ${newStatus}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {["all", "SUBMITTED", "UNDER_REVIEW", "LIVE", "REJECTED", "SUSPENDED"].map(s => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setStatus(s)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs border ${status === s ? "bg-[#2563EB] border-[#2563EB]" : "border-slate-200 text-[#475569]"}`}>{s}</button>
        ))}
      </div>
      <div className="tl-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200"><tr>{fields.map(f => <th key={f} className="text-left p-3 tl-label">{f}</th>)}<th className="p-3 tl-label">Actions</th></tr></thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={fields.length+1} className="p-8 text-center text-[#475569]">No records</td></tr> :
                items.map(it => (
                  <tr key={it.id} className="border-b border-white/[0.04]">
                    {fields.map(f => <td key={f} className="p-3">{f === "price" ? currency(it[f]) : String(it[f] || "—")}</td>)}
                    <td className="p-3">
                      <select data-testid={`status-select-${it.id}`} defaultValue="" onChange={(e) => e.target.value && update(it.id, e.target.value)} className="tl-input py-1.5 text-xs">
                        <option value="">Change status…</option>
                        {STATUS_ACTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UsersList() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then(r => setUsers(r.data)); }, []);
  const setRole = async (id, role) => { await api.put(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); api.get("/admin/users").then(r => setUsers(r.data)); };
  return (
    <div className="tl-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200"><tr><th className="text-left p-3 tl-label">Email</th><th className="text-left p-3 tl-label">Name</th><th className="text-left p-3 tl-label">Role</th><th className="p-3 tl-label">Action</th></tr></thead>
          <tbody>{users.map(u => (
            <tr key={u.id} className="border-b border-white/[0.04]">
              <td className="p-3">{u.email}</td><td className="p-3">{u.name}</td><td className="p-3"><span className="tl-chip">{u.role}</span></td>
              <td className="p-3"><select data-testid={`role-select-${u.id}`} defaultValue="" onChange={e => e.target.value && setRole(u.id, e.target.value)} className="tl-input py-1.5 text-xs"><option value="">Change role…</option>{["CUSTOMER","EXPERT","PRINT_PARTNER","ADMIN"].map(r => <option key={r}>{r}</option>)}</select></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function OrdersList() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then(r => setOrders(r.data)); }, []);
  return (
    <div className="tl-card overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
      <thead className="border-b border-slate-200"><tr><th className="text-left p-3 tl-label">Order</th><th className="text-left p-3 tl-label">Customer</th><th className="text-left p-3 tl-label">Total</th><th className="text-left p-3 tl-label">Status</th></tr></thead>
      <tbody>{orders.map(o => (<tr key={o.id} className="border-b border-white/[0.04]"><td className="p-3 font-mono text-xs">{o.id.slice(0,8)}</td><td className="p-3">{o.user_email}</td><td className="p-3">{currency(o.total)}</td><td className="p-3"><span className="tl-chip">{o.status}</span></td></tr>))}
      {orders.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#475569]">No orders yet</td></tr>}
      </tbody></table></div></div>
  );
}

function ReviewsList() {
  const [reviews, setReviews] = useState([]);
  const load = () => api.get("/admin/reviews?status=PENDING").then(r => setReviews(r.data));
  useEffect(load, []);
  const update = async (id, s) => { await api.put(`/admin/reviews/${id}/status`, { status: s }); load(); };
  return (
    <div className="space-y-3">
      {reviews.length === 0 ? <div className="tl-card p-8 text-center text-[#475569]">No pending reviews</div> : reviews.map(r => (
        <div key={r.id} className="tl-card p-4">
          <div className="flex justify-between"><div><div className="font-semibold">{r.title}</div><div className="text-xs text-[#475569]">{r.rating}★ by {r.user_name} · {r.target_type}</div></div>
            <div className="flex gap-2"><button data-testid={`approve-${r.id}`} onClick={() => update(r.id, "APPROVED")} className="tl-btn-primary text-xs">Approve</button><button onClick={() => update(r.id, "REJECTED")} className="tl-btn-ghost text-xs">Reject</button></div>
          </div>
          <div className="text-sm text-[#475569] mt-2">{r.comment}</div>
        </div>
      ))}
    </div>
  );
}
